import React from 'react';
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "theory-of-production-returns-to-scale-cost-curves-notes",
  title: "Theory of Production: Short Run, Long Run, Returns to Scale, Isoquants & Cost Curves Notes",
  description: "Detailed notes on Theory of Production, Returns to Scale, Isoquant properties, and Cost Curves with solved numericals.",
  pubDate: "2025-08-21",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Microeconomics", "Production Theory", "Cost Curves", "Isoquants"],
  readMinutes: 15,
  heroEyebrow: "Microeconomics Notes"
};

export const Body = () => {
  return (
    <article>
      <h2>Theory of Production</h2>
      <p>Production is the process of transforming inputs into outputs. The production function expresses the technological relationship between physical inputs and physical output. Q = f(K, L), where K is capital and L is labor.</p>
      
      <h3>Short Run vs Long Run</h3>
      <p>The Short Run is a period in which at least one factor of production is fixed (usually capital). Firms can only vary output by changing variable inputs (labor). The Long Run is a period where all factors of production are variable.</p>

      <h2>Law of Variable Proportions (Short Run)</h2>
      <p>As we add successive units of a variable factor to fixed factors, total product initially increases at an increasing rate, then at a decreasing rate, and eventually falls. This yields three stages:</p>
      <p>1. Increasing Returns: MP (Marginal Product) is rising. AP (Average Product) is rising.</p>
      <p>2. Diminishing Returns: MP is falling but positive. AP is falling. TP is increasing at a decreasing rate.</p>
      <p>3. Negative Returns: MP is negative. TP is falling.</p>

      <h2>Isoquants and Isocost Lines (Long Run)</h2>
      <h3>Isoquant Properties</h3>
      <p>An isoquant shows all combinations of inputs that yield the same level of output.</p>
      <ul>
        <li>Downward sloping: Due to substitutability of inputs.</li>
        <li>Convex to origin: Due to diminishing Marginal Rate of Technical Substitution (MRTS).</li>
        <li>Higher isoquant means higher output.</li>
        <li>They do not intersect.</li>
      </ul>

      <h3>Producer Equilibrium</h3>
      <p>The isocost line represents all combinations of inputs a firm can buy given its budget and input prices: C = wL + rK. The firm minimizes cost or maximizes output where the isocost line is tangent to the highest isoquant.</p>
      <p>Equilibrium Condition: MRTS = MP_L / MP_K = w / r</p>

      <h2>Returns to Scale</h2>
      <p>Returns to scale describe how output changes when all inputs are increased proportionately (long run).</p>
      <p>1. Constant Returns to Scale (CRS): Output increases by the same proportion.</p>
      <p>2. Increasing Returns to Scale (IRS): Output increases by a greater proportion. Driven by economies of scale (specialization, dimensional efficiencies).</p>
      <p>3. Decreasing Returns to Scale (DRS): Output increases by a smaller proportion. Driven by diseconomies of scale (management complexities).</p>
      
      <h3>Cobb-Douglas Production Function</h3>
      <p>Q = A * (L^alpha) * (K^beta)</p>
      <p>If alpha + beta = 1, CRS. If alpha + beta &gt; 1, IRS. If alpha + beta &lt; 1, DRS.</p>

      <h2>Cost Curves</h2>
      <h3>Short-Run Cost Curves</h3>
      <p>TC = TFC + TVC.</p>
      <p>ATC = AFC + AVC.</p>
      <p>MC = Change in TC / Change in Q.</p>
      <p>The MC curve intersects both AVC and ATC at their minimum points. The shape of these curves is U-shaped due to the Law of Variable Proportions.</p>

      <h3>Long-Run Cost Curves</h3>
      <p>The Long Run Average Cost (LAC) curve is the envelope of short-run average cost (SAC) curves. It is U-shaped due to economies and diseconomies of scale.</p>

      <h2>Solved Numericals</h2>
      <h3>Problem 1: MRTS and Equilibrium</h3>
      <p>Q = 100 * (L^0.5) * (K^0.5). w = 10, r = 40. Budget = 800.</p>
      <p>Solution:</p>
      <p>MP_L = 50 * (L^-0.5) * (K^0.5) = 50 * (K/L)^0.5</p>
      <p>MP_K = 50 * (L^0.5) * (K^-0.5) = 50 * (L/K)^0.5</p>
      <p>MRTS = MP_L / MP_K = K / L.</p>
      <p>Equilibrium: MRTS = w/r =&gt; K/L = 10/40 = 1/4 =&gt; L = 4K.</p>
      <p>Budget constraint: 10L + 40K = 800 =&gt; 10(4K) + 40K = 800 =&gt; 80K = 800 =&gt; K = 10.</p>
      <p>L = 40.</p>

    </article>
  );
};


